import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import Utils from "../Utils";

const getNodeByPath = (root, pathArr) => {
  let node = root;
  for (const part of pathArr) {
    if (!node) return null;
    const found = node.folders?.find((folder) => folder.name === part);
    if (!found) return null;
    node = found;
  }
  return node;
};

const Breadcrumbs = ({ path, onNavigate }) => (
  <nav className="mb-4 flex items-center gap-2 text-sm">
    <span
      className={
        path.length === 0
          ? "font-bold"
          : "cursor-pointer text-blue-600 hover:underline"
      }
      onClick={() => path.length > 0 && onNavigate([])}
    >
      Archive Home
    </span>
    {path.map((part, idx) => (
      <React.Fragment key={`${part}-${idx}`}>
        <span>/</span>
        <span
          className={
            idx === path.length - 1
              ? "font-bold"
              : "cursor-pointer text-blue-600 hover:underline"
          }
          onClick={() =>
            idx !== path.length - 1 && onNavigate(path.slice(0, idx + 1))
          }
        >
          {part}
        </span>
      </React.Fragment>
    ))}
  </nav>
);

const ArchiveGrid = ({ folders, files, onFolderClick }) => (
  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
    {folders?.map((folder) => (
      <div
        key={folder.name}
        className="flex cursor-pointer flex-col items-center rounded bg-gray-50 p-4 hover:bg-blue-50"
        onClick={() => onFolderClick(folder.name)}
      >
        <i className="fas fa-folder text-3xl text-yellow-500"></i>
        <span
          className="mt-2 w-full break-words text-center font-medium leading-snug"
          title={folder.name}
        >
          {folder.name}
        </span>
      </div>
    ))}
    {files?.map((file) => {
      let iconClass = "fa-file text-gray-400";
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith(".pdf")) iconClass = "fa-file-pdf text-red-600";
      else if (
        lowerName.endsWith(".jpg") ||
        lowerName.endsWith(".png") ||
        lowerName.endsWith(".jpeg")
      )
        iconClass = "fa-image text-yellow-400";
      else if (lowerName.endsWith(".zip"))
        iconClass = "fa-file-archive text-black-400";
      else if (lowerName.endsWith(".mp4")) iconClass = "fa-video text-red-600";
      else if (lowerName.endsWith(".docx"))
        iconClass = "fa-file-word text-blue-600";
      else if (lowerName.endsWith(".xlsx"))
        iconClass = "fa-file-excel text-green-600";

      const href = Utils.apiUrl(
        `archives/download?url=${encodeURIComponent(file.url)}&name=${encodeURIComponent(file.name)}`,
      );

      return (
        <div
          key={`${file.name}-${file.url}`}
          className="flex flex-col items-center rounded bg-gray-50 p-4"
        >
          <a href={href} className="flex w-full flex-col items-center">
            <i className={`fas ${iconClass} text-3xl`}></i>
            <span
              className="mt-2 w-full break-words text-center leading-snug text-blue-600"
              title={file.name}
            >
              {file.name}
            </span>
            <span className="mt-1 text-xs text-gray-400">
              {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : ""}
            </span>
            <span className="text-xs text-gray-400">
              {file.createdOn
                ? moment(file.createdOn).format("YYYY-MM-DD")
                : ""}
            </span>
          </a>
        </div>
      );
    })}
  </div>
);

const Archives = () => {
  const [list, setList] = useState({
    loading: false,
    error: false,
    data: null,
  });
  const [path, setPath] = useState([]);

  useEffect(() => {
    if (!Utils.hasArchiveAccess()) {
      return;
    }

    setList((prev) => ({ ...prev, loading: true }));
    axios
      .get(Utils.apiUrl("archives/tree"), Utils.apiHeaders())
      .then((res) => {
        if (res.data.success) {
          setList({ loading: false, error: false, data: res.data.data });
        } else {
          setList({
            loading: false,
            error: res.data.message || "Failed to load archives.",
            data: null,
          });
        }
      })
      .catch((err) => {
        setList({
          loading: false,
          error: err.message || "Server error",
          data: null,
        });
      });
  }, []);

  if (!Utils.hasArchiveAccess()) {
    return (
      <div className="m-6 rounded border border-red-200 bg-red-50 p-6 text-red-700">
        You do not have permission to access archives.
      </div>
    );
  }

  let node = list.data;
  if (path.length > 0 && node) {
    node = getNodeByPath(node, path);
  }

  if (list.loading) {
    return <div className="p-6">Loading archives...</div>;
  }

  if (list.error) {
    return (
      <div className="p-6 text-red-600">
        Error loading archives: {JSON.stringify(list.error)}
      </div>
    );
  }

  if (!node) {
    return <div className="p-6">No archives found.</div>;
  }

  return (
    <div className="rounded bg-white shadow">
      <div className="p-6">
        <h1 className="mb-4 flex items-center gap-2 text-2xl font-bold">
          <i className="fas fa-box-archive"></i>
          Photography Archives
        </h1>
        <p className="mb-4 text-sm text-gray-500">Archived photoshoots.</p>
        <Breadcrumbs path={path} onNavigate={setPath} />
        <ArchiveGrid
          folders={node.folders}
          files={node.files}
          onFolderClick={(folderName) => setPath([...path, folderName])}
        />
      </div>
    </div>
  );
};

export default Archives;
